import { useState, useEffect } from 'react';
import { BREAKPOINTS } from '../constants/index.js';

// Hook for responsive breakpoint detection
export const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState(() => {
    // Return default for SSR and initial render
    if (typeof window === 'undefined' || !window.innerWidth) {
      return 'desktop';
    }
    
    try {
      const width = window.innerWidth;
      if (width >= BREAKPOINTS.DESKTOP) return 'desktop';
      if (width >= BREAKPOINTS.TABLET_LG) return 'tabletLg';
      if (width >= BREAKPOINTS.TABLET_MD) return 'tabletMd';
      if (width >= BREAKPOINTS.TABLET_SM) return 'tabletSm';
      if (width >= BREAKPOINTS.MOBILE_MD) return 'mobile';
      return 'mobileSm';
    } catch (error) {
      console.warn('Error detecting breakpoint:', error);
      return 'desktop';
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      try {
        const width = window.innerWidth;
        let newBreakpoint;
        
        if (width >= BREAKPOINTS.DESKTOP) newBreakpoint = 'desktop';
        else if (width >= BREAKPOINTS.TABLET_LG) newBreakpoint = 'tabletLg';
        else if (width >= BREAKPOINTS.TABLET_MD) newBreakpoint = 'tabletMd';
        else if (width >= BREAKPOINTS.TABLET_SM) newBreakpoint = 'tabletSm';
        else if (width >= BREAKPOINTS.MOBILE_MD) newBreakpoint = 'mobile';
        else newBreakpoint = 'mobileSm';
        
        setBreakpoint(newBreakpoint);
      } catch (error) {
        console.warn('Error in resize handler:', error);
      }
    };

    // Initial call to set correct breakpoint after mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
};

// Helper function to calculate device type based on width
const calculateDeviceType = (width) => {
  if (width >= BREAKPOINTS.DESKTOP) return 'desktop';     // 1200px+
  if (width >= BREAKPOINTS.TABLET_SM) return 'tablet';   // 768px - 1199px
  if (width >= BREAKPOINTS.SURFACE_DUO) return 'tablet'; // 540px - 767px
  return 'mobile';                                        // 0px - 539px
};

// Enhanced device type detection with better hydration handling
export const useDeviceType = () => {
  const [deviceType, setDeviceType] = useState('desktop'); // Always start with desktop to prevent flashing
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // This effect runs only on the client after hydration
    setIsHydrated(true);
    
    if (typeof window === 'undefined') return;

    const detectDeviceType = () => {
      try {
        const width = window.innerWidth;
        const detectedType = calculateDeviceType(width);
        
        console.log('🔍 Device Detection:', {
          width,
          DESKTOP_BREAKPOINT: BREAKPOINTS?.DESKTOP,
          TABLET_SM_BREAKPOINT: BREAKPOINTS?.TABLET_SM,
          SURFACE_DUO_BREAKPOINT: BREAKPOINTS?.SURFACE_DUO,
          detected: detectedType,
          isDesktop: width >= (BREAKPOINTS?.DESKTOP || 1200),
          isTablet: width >= (BREAKPOINTS?.TABLET_SM || 768) && width < (BREAKPOINTS?.DESKTOP || 1200),
          isMobile: width < (BREAKPOINTS?.TABLET_SM || 768)
        });
        
        setDeviceType(prevType => {
          if (prevType !== detectedType) {
            console.log(`📱 Device type changed: ${prevType} → ${detectedType}`);
          }
          return detectedType;
        });
      } catch (error) {
        console.warn('Error detecting device type:', error);
      }
    };

    // Initial detection
    detectDeviceType();

    // Listen for resize events
    window.addEventListener('resize', detectDeviceType);
    
    // Also listen for orientation changes on mobile devices
    window.addEventListener('orientationchange', () => {
      // Small delay to ensure window.innerWidth is updated
      setTimeout(detectDeviceType, 100);
    });

    return () => {
      window.removeEventListener('resize', detectDeviceType);
      window.removeEventListener('orientationchange', detectDeviceType);
    };
  }, []);

  // Return desktop during SSR and initial render to prevent hydration mismatches
  return isHydrated ? deviceType : 'desktop';
};

// Hook for media query matching
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    
    try {
      return window.matchMedia(query).matches;
    } catch (error) {
      console.warn('Error with media query:', query, error);
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    try {
      const mediaQuery = window.matchMedia(query);
      const handler = (event) => setMatches(event.matches);
      
      // Use addEventListener if available (modern browsers)
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handler);
        return () => mediaQuery.removeListener(handler);
      }
    } catch (error) {
      console.warn('Error setting up media query listener:', error);
    }
  }, [query]);

  return matches;
};

// Hook for window dimensions
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(() => {
    if (typeof window === 'undefined') {
      return { width: 1920, height: 1080 };
    }
    
    try {
      return { 
        width: window.innerWidth || 1920, 
        height: window.innerHeight || 1080 
      };
    } catch (error) {
      console.warn('Error getting window size:', error);
      return { width: 1920, height: 1080 };
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      try {
        setWindowSize({
          width: window.innerWidth || 1920,
          height: window.innerHeight || 1080,
        });
      } catch (error) {
        console.warn('Error in window resize handler:', error);
      }
    };

    // Initial call to set correct window size after mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

// Hook for copy to clipboard with state management
export const useCopyToClipboard = (resetDelay = 2000) => {
  const [copied, setCopied] = useState(false);

  const copy = async (text) => {
    try {
      // Check if clipboard API is available
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), resetDelay);
        return { success: true };
      } else {
        // Fallback for older browsers
        console.warn('Clipboard API not available');
        return { success: false, error: 'Clipboard not supported' };
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      return { success: false, error };
    }
  };

  return [copied, copy];
};

// Hook for zoom-aware responsive design
export const useZoomAwareViewport = () => {
  const [viewportInfo, setViewportInfo] = useState(() => {
    if (typeof window === 'undefined') {
      return {
        width: 1920,
        height: 1080,
        zoomLevel: 1,
        effectiveWidth: 1920,
        effectiveHeight: 1080,
        isZoomed: false
      };
    }
    
    try {
      // Calculate zoom level using multiple methods
      const outerInnerRatio = window.outerWidth && window.innerWidth 
        ? window.outerWidth / window.innerWidth 
        : 1;
      
      const devicePixelRatio = window.devicePixelRatio || 1;
      
      let zoomLevel = 1;
      if (outerInnerRatio > 1 && outerInnerRatio < 10) {
        zoomLevel = outerInnerRatio;
      } else if (devicePixelRatio > 1) {
        zoomLevel = devicePixelRatio > 2 ? devicePixelRatio / 2 : devicePixelRatio;
      }
      
      // Clamp zoom level
      zoomLevel = Math.min(Math.max(zoomLevel, 0.25), 5);
      
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      return {
        width,
        height,
        zoomLevel,
        effectiveWidth: width * zoomLevel,
        effectiveHeight: height * zoomLevel,
        isZoomed: Math.abs(zoomLevel - 1) > 0.1
      };
    } catch (error) {
      console.warn('Error calculating zoom-aware viewport:', error);
      return {
        width: 1920,
        height: 1080,
        zoomLevel: 1,
        effectiveWidth: 1920,
        effectiveHeight: 1080,
        isZoomed: false
      };
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateViewportInfo = () => {
      try {
        const outerInnerRatio = window.outerWidth && window.innerWidth 
          ? window.outerWidth / window.innerWidth 
          : 1;
        
        const devicePixelRatio = window.devicePixelRatio || 1;
        
        let zoomLevel = 1;
        if (outerInnerRatio > 1 && outerInnerRatio < 10) {
          zoomLevel = outerInnerRatio;
        } else if (devicePixelRatio > 1) {
          zoomLevel = devicePixelRatio > 2 ? devicePixelRatio / 2 : devicePixelRatio;
        }
        
        zoomLevel = Math.min(Math.max(zoomLevel, 0.25), 5);
        
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        setViewportInfo({
          width,
          height,
          zoomLevel,
          effectiveWidth: width * zoomLevel,
          effectiveHeight: height * zoomLevel,
          isZoomed: Math.abs(zoomLevel - 1) > 0.1
        });
      } catch (error) {
        console.warn('Error updating zoom-aware viewport:', error);
      }
    };

    // Update on window resize and zoom
    window.addEventListener('resize', updateViewportInfo);
    window.addEventListener('wheel', updateViewportInfo, { passive: true });
    
    // Initial update
    updateViewportInfo();

    return () => {
      window.removeEventListener('resize', updateViewportInfo);
      window.removeEventListener('wheel', updateViewportInfo);
    };
  }, []);

  return viewportInfo;
};